import React from 'react';
import { CheckCircle2, XCircle, AlertCircle, Circle } from 'lucide-react';

const VERDICT_CONFIG = {
  new:               { color: 'var(--color-new)',              label: 'Analyzing…',  Icon: Circle },
  neutral:           { color: 'var(--color-neutral-bar)',      label: 'Unverified',  Icon: Circle },
  verified:          { color: 'var(--color-verified-bar)',     label: 'Verified',    Icon: CheckCircle2 },
  disputed:          { color: 'var(--color-disputed-bar)',     label: 'Disputed',    Icon: AlertCircle },
  invalidated:       { color: 'var(--color-invalidated-bar)', label: 'Invalidated', Icon: XCircle },
  mostly_verified:   { Icon: CheckCircle2 },
  mostly_invalidated:{ Icon: XCircle },
  mostly_disputed:   { Icon: AlertCircle },
  half_invalidated:  { Icon: XCircle },
  half_disputed:     { Icon: AlertCircle },
  // Move 3: some main claims decided, others still untested
  partly_verified:   { color: 'var(--color-verified-bar)',     label: 'Partly verified',    Icon: CheckCircle2 },
  partly_invalidated:{ color: 'var(--color-invalidated-bar)', label: 'Partly invalidated', Icon: XCircle },
  partly_disputed:   { color: 'var(--color-disputed-bar)',     label: 'Partly disputed',    Icon: AlertCircle },
  // Prediction verdicts (per-claim)
  plausible:         { color: '#7c3aed', label: 'Plausible',   Icon: CheckCircle2 },
  contested:         { color: 'var(--color-disputed)', label: 'Contested', Icon: AlertCircle },
  implausible:       { color: '#374151', label: 'Implausible', Icon: XCircle },
  // FIX 3: Post-level prediction verdicts
  likely:            { color: '#15803d', label: 'Likely',      Icon: CheckCircle2 },
  unlikely:          { color: '#dc2626', label: 'Unlikely',    Icon: XCircle },
  unverified:        { color: '#6b7280', label: 'Unverified',  Icon: Circle },
};

/** For argument nodes (green/red/neutral status + is_new flag) */
export function getVerdict(rootStatus, isNew) {
  if (isNew) return 'new';
  if (!rootStatus) return 'disputed';
  if (rootStatus === 'green') return 'verified';
  if (rootStatus === 'red') return 'invalidated';
  return 'disputed';
}

/** For post-level verdicts — uses the stored verdict field, falls back to deriving from root_status */
export function getPostVerdict(post) {
  if (!post) return 'new';
  // Always honour the stored verdict first — post.is_new only means "recently created and not yet
  // viewed", NOT "not yet analyzed". A post analyzed in <20 min would incorrectly return 'new'
  // if we checked is_new before the stored verdict.
  if (post.verdict && post.verdict !== 'new') return post.verdict;
  // No stored verdict (or explicitly 'new') → show as analyzing/unverified
  return 'new';
}

/** Main claims decide the headline; if none is marked main, all claims count. */
function mainClaims(claimStatuses) {
  const all = claimStatuses || [];
  return all.some(cs => cs.is_primary) ? all.filter(cs => cs.is_primary) : all;
}

/**
 * Side points that were decided while the headline follows the main claims,
 * e.g. "1 side point invalidated". Empty when there is nothing to note.
 */
export function sidePointNote(claimStatuses) {
  const all = claimStatuses || [];
  if (!all.some(cs => cs.is_primary)) return '';
  const side = all.filter(cs => !cs.is_primary);
  const inv = side.filter(cs => cs.status === 'invalidated' || cs.status === 'implausible').length;
  const dis = side.filter(cs => cs.status === 'disputed' || cs.status === 'contested').length;
  const parts = [];
  if (inv) parts.push(`${inv} side point${inv > 1 ? 's' : ''} invalidated`);
  if (dis) parts.push(`${dis} side point${dis > 1 ? 's' : ''} disputed`);
  return parts.join(', ');
}

const COLOR = {
  verified:    'var(--color-verified-bar)',
  disputed:    'var(--color-disputed-bar)',
  invalidated: 'var(--color-invalidated-bar)',
};

/**
 * Sort claim-status buckets for gradient display.
 * Disputed always goes LAST (rightmost) — it represents unresolved points and is visually secondary.
 * Non-disputed buckets are sorted by count desc, tie-break: invalidated > verified.
 */
function sortedBuckets(verCount, dispCount, invCount) {
  const nonDisputed = [
    { key: 'invalidated', count: invCount },
    { key: 'verified',    count: verCount },
  ]
    .filter(s => s.count > 0)
    .sort((a, b) => b.count - a.count || (a.key === 'invalidated' ? -1 : 1));

  return dispCount > 0
    ? [...nonDisputed, { key: 'disputed', count: dispCount }]
    : nonDisputed;
}

/**
 * Compute natural-language label and icon from claim counts.
 * Used for mixed (mostly_* / half_*) verdicts.
 */
function computeMixedLabel(verC, dispC, invC, verdict) {
  const hasVer = verC > 0, hasDist = dispC > 0, hasInv = invC > 0;

  if (verdict === 'half_invalidated') return { label: 'Half invalidated, half disputed', Icon: XCircle };
  if (verdict === 'half_disputed')    return { label: 'Half disputed',                   Icon: AlertCircle };

  // Three-bucket (all three states present)
  if (hasInv && hasVer && hasDist) return { label: 'Partially verified, partially invalidated', Icon: AlertCircle };

  // inv + ver only (no disputed)
  if (hasInv && hasVer) {
    if (invC >= verC) return { label: 'Mostly invalidated', Icon: XCircle };
    return { label: 'Mostly verified', Icon: CheckCircle2 };
  }

  // ver + disp (no invalidated)
  if (hasVer && hasDist) {
    if (verC > dispC) return { label: 'Verified, but some points disputed', Icon: CheckCircle2 };
    return { label: 'Verified but partially disputed', Icon: AlertCircle };
  }

  // inv + disp (no verified)
  if (hasInv && hasDist) {
    if (invC > dispC) return { label: 'Mostly invalidated', Icon: XCircle };
    return { label: 'Invalidated and disputed', Icon: AlertCircle };
  }

  // Single-bucket fallback
  if (hasVer)  return { label: 'Verified',    Icon: CheckCircle2 };
  if (hasInv)  return { label: 'Invalidated', Icon: XCircle };
  return { label: 'Disputed', Icon: AlertCircle };
}

/**
 * Compute CSS background for the verdict bar.
 * Disputed always goes RIGHT. Non-disputed sorted by count desc, tie-break: inv > ver.
 */
function computeBackground(verdict, claimStatuses) {
  const cfg = VERDICT_CONFIG[verdict] || VERDICT_CONFIG.disputed;

  if (verdict.startsWith('partly_')) {
    const main = mainClaims(claimStatuses);
    const key = verdict.replace('partly_', '');
    const decided = main.filter(cs => cs.status === key).length;
    const pct = main.length ? Math.round((decided / main.length) * 100) : 50;
    return `linear-gradient(90deg, ${cfg.color} ${pct}%, var(--color-neutral-bar) ${pct}%)`;
  }

  if (!verdict.startsWith('mostly_') && !verdict.startsWith('half_')) {
    return cfg.color || 'var(--color-unverified-text)';
  }

  const normSt = (s) => s === 'definition_verified' ? 'verified' : (s === 'definition_submitted' || s === 'pending_definition') ? 'disputed' : s;
  const statuses = claimStatuses || [];
  const verCount  = statuses.filter(cs => normSt(cs.status) === 'verified').length;
  const dispCount = statuses.filter(cs => normSt(cs.status) === 'disputed').length;
  const invCount  = statuses.filter(cs => normSt(cs.status) === 'invalidated').length;
  const total = verCount + dispCount + invCount || 1;

  const buckets = sortedBuckets(verCount, dispCount, invCount);

  if (buckets.length === 0) return cfg.color || 'var(--color-disputed)';
  if (buckets.length === 1) return COLOR[buckets[0].key];

  if (buckets.length === 3) {
    const p1 = Math.round((buckets[0].count / total) * 100);
    const p2 = Math.round(((buckets[0].count + buckets[1].count) / total) * 100);
    return (
      `linear-gradient(90deg, ` +
      `${COLOR[buckets[0].key]} 0%, ${COLOR[buckets[0].key]} ${p1}%, ` +
      `${COLOR[buckets[1].key]} ${p1}%, ${COLOR[buckets[1].key]} ${p2}%, ` +
      `${COLOR[buckets[2].key]} ${p2}%, ${COLOR[buckets[2].key]} 100%)`
    );
  }

  // Two buckets
  const leftPct = Math.round((buckets[0].count / total) * 100);
  return `linear-gradient(90deg, ${COLOR[buckets[0].key]} ${leftPct}%, ${COLOR[buckets[1].key]} ${leftPct}%)`;
}

/**
 * Compute card background from claim statuses with strict priority:
 * 1. Any invalidated → --color-invalidated-bg
 * 2. Any verified (no invalidated) → --color-verified-bg
 * 3. All disputed → --color-disputed-bg
 * 4. Falls back to verdictFallback
 */
export function getCardBgFromClaims(claimStatuses, verdictFallback) {
  if (claimStatuses && claimStatuses.length > 0) {
    const norm = (s) => s === 'definition_verified' ? 'verified'
      : (s === 'definition_submitted' || s === 'pending_definition') ? 'disputed' : s;
    const statuses = claimStatuses.map(cs => norm(cs.status)).filter(Boolean);
    if (statuses.length > 0) {
      if (statuses.includes('invalidated')) return 'var(--color-invalidated-bg)';
      if (statuses.includes('verified'))    return 'var(--color-verified-bg)';
      if (statuses.every(s => s === 'disputed')) return 'var(--color-disputed-bg)';
    }
  }
  return verdictFallback || 'var(--color-surface)';
}

/**
 * Returns the card background color for a post card based on verdict.
 */
export function getPartialCardBg(verdict) {
  if (!verdict) return null;
  if (verdict === 'mostly_verified')                               return 'var(--color-verified-bg)';
  if (verdict === 'mostly_invalidated' || verdict === 'half_invalidated') return 'var(--color-invalidated-bg)';
  if (verdict === 'mostly_disputed'    || verdict === 'half_disputed')    return 'var(--color-disputed-bg)';
  if (verdict === 'plausible')   return 'rgba(124,58,237,0.08)';
  if (verdict === 'implausible') return 'rgba(55,65,81,0.08)';
  if (verdict === 'contested')   return 'var(--color-disputed-bg)';
  if (verdict === 'likely')      return 'var(--color-verified-bg)';
  if (verdict === 'unlikely')    return 'var(--color-invalidated-bg)';
  if (verdict === 'unverified')  return 'var(--color-surface)';
  if (verdict === 'partly_verified')    return 'var(--color-verified-bg)';
  if (verdict === 'partly_invalidated') return 'var(--color-invalidated-bg)';
  if (verdict === 'partly_disputed')    return 'var(--color-disputed-bg)';
  return null;
}

export default function VerdictBar({ verdict, counterArg, moreCount, rounded = true, claimStatuses, predictionVerdict }) {
  const cfg = VERDICT_CONFIG[verdict] || VERDICT_CONFIG.disputed;
  const bg  = computeBackground(verdict, claimStatuses);

  // Normalize definition-related claim statuses into the three base buckets
  const normStatus = (s) => {
    if (s === 'definition_verified') return 'verified';
    if (s === 'definition_submitted') return 'disputed';
    if (s === 'pending_definition') return 'disputed';
    return s;
  };
  const statuses = claimStatuses || [];
  const verC  = statuses.filter(cs => normStatus(cs.status) === 'verified').length;
  const dispC = statuses.filter(cs => normStatus(cs.status) === 'disputed').length;
  const invC  = statuses.filter(cs => normStatus(cs.status) === 'invalidated').length;

  let displayLabel = cfg.label || 'Disputed';
  let DisplayIcon  = cfg.Icon || AlertCircle;

  if (verdict.startsWith('mostly_') || verdict.startsWith('half_')) {
    const mixed = computeMixedLabel(verC, dispC, invC, verdict);
    displayLabel = mixed.label;
    DisplayIcon  = mixed.Icon;
  }

  if (verdict.startsWith('partly_')) {
    const main = mainClaims(claimStatuses);
    const key = verdict.replace('partly_', '');
    const n = main.filter(cs => normStatus(cs.status) === key).length;
    if (main.length > 1) displayLabel = `${cfg.label} (${n} of ${main.length} main claims)`;
  }

  const sideNote = sidePointNote(claimStatuses);

  // Mixed labels are sentence case; simple labels get uppercase treatment via CSS
  const isMixed = verdict.startsWith('mostly_') || verdict.startsWith('half_') || verdict.startsWith('partly_');
  // Neutral (#94a3b8) and disputed (#f59e0b) bars are too light for white text — use dark text
  const needsDarkText = verdict === 'neutral' || verdict === 'disputed' ||
    verdict === 'mostly_disputed' || verdict === 'half_disputed' ||
    verdict === 'contested' || verdict === 'unverified';
  const textColor = needsDarkText ? '#1e293b' : '#ffffff';

  return (
    <div style={{
      minHeight: 32,
      background: bg,
      color: textColor,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      padding: '6px 12px',
      borderRadius: rounded ? '8px 8px 0 0' : 0,
      flexShrink: 0,
      gap: 8,
    }}>
      <span style={{
        fontFamily: 'var(--font-body)',
        fontSize: 12,
        fontWeight: 600,
        textTransform: isMixed ? 'none' : 'uppercase',
        letterSpacing: isMixed ? '0.01em' : '0.06em',
        color: textColor,
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        flexShrink: 0,
        paddingTop: 1,
      }}>
        <DisplayIcon size={13} strokeWidth={2.5} />
        {displayLabel}
        {sideNote && (
          <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, opacity: 0.9 }}>
            · {sideNote}
          </span>
        )}
      </span>
      {verdict === 'invalidated' && counterArg && (
        <span style={{
          fontFamily: 'var(--font-body)',
          fontSize: 11,
          color: '#ffffff',
          opacity: 0.9,
          maxWidth: '65%',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.4,
          textAlign: 'right',
        }}>
          {counterArg}{moreCount > 0 ? ` +${moreCount} more` : ''}
        </span>
      )}
      {/* Prediction verdict is now shown BELOW the verdict bar by callers, not inside it */}
    </div>
  );
}
